import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap'
import { useToast } from '../../components/ToastProvider'
import {
  fetchPimMasterCategories,
  fetchProductPimCategoriesEffective,
  saveProductPimCategoryAdjustments,
  type PimMasterCategoryDto,
} from '../../services/api'

export default function ProductPimCategoriesPanel({ productId }: { productId: string }) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [excludeIds, setExcludeIds] = useState<Set<string>>(new Set())
  const [includeIds, setIncludeIds] = useState<Set<string>>(new Set())

  const mastersQuery = useQuery({
    queryKey: ['pim-master-categories'],
    queryFn: () => fetchPimMasterCategories(false),
  })

  const effectiveQuery = useQuery({
    queryKey: ['pim-product-effective', productId],
    queryFn: () => fetchProductPimCategoriesEffective(productId),
    enabled: !!productId,
  })

  const masters = mastersQuery.data ?? []
  const effective = effectiveQuery.data

  useEffect(() => {
    if (!effective) return
    setExcludeIds(new Set(effective.excludedFromFamily.map((c) => c.id)))
    setIncludeIds(new Set(effective.includedExtras.map((c) => c.id)))
  }, [effective])

  const saveMutation = useMutation({
    mutationFn: () =>
      saveProductPimCategoryAdjustments(productId, {
        excludeMasterCategoryIds: Array.from(excludeIds),
        includeExtraMasterCategoryIds: Array.from(includeIds),
      }),
    onSuccess: async () => {
      showToast('Product PIM categories saved.', { variant: 'success' })
      await queryClient.invalidateQueries({ queryKey: ['pim-product-effective', productId] })
    },
    onError: () => showToast('Failed to save product categories.', { variant: 'danger' }),
  })

  const familyCodeSet = useMemo(() => {
    const s = new Set<string>()
    if (effective) {
      for (const c of effective.familyCategories) s.add(c.code)
    }
    return s
  }, [effective])

  const extraCandidates = useMemo(
    () => masters.filter((m) => m.isActive && !familyCodeSet.has(m.code)),
    [masters, familyCodeSet],
  )

  const toggle = (id: string, which: 'exclude' | 'include', checked: boolean) => {
    if (which === 'exclude') {
      setExcludeIds((prev) => {
        const next = new Set(prev)
        if (checked) next.add(id)
        else next.delete(id)
        return next
      })
      if (checked) {
        setIncludeIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    } else {
      setIncludeIds((prev) => {
        const next = new Set(prev)
        if (checked) next.add(id)
        else next.delete(id)
        return next
      })
      if (checked) {
        setExcludeIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    }
  }

  if (mastersQuery.isPending || effectiveQuery.isPending) {
    return (
      <Card className="mb-3">
        <Card.Body className="py-3 d-flex align-items-center gap-2 text-muted small">
          <Spinner animation="border" size="sm" />
          Loading PIM categories…
        </Card.Body>
      </Card>
    )
  }

  if (mastersQuery.isError || effectiveQuery.isError) {
    return (
      <Alert variant="danger" className="mb-3">
        <div className="fw-semibold mb-1">Could not load PIM categories</div>
        <div className="small mb-2">
          The catalog API failed (often HTTP 500 until the database has PIM tables and the API is restarted).
          Without this data you cannot adjust categories here.
        </div>
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => {
            void mastersQuery.refetch()
            void effectiveQuery.refetch()
          }}
        >
          Retry
        </Button>
      </Alert>
    )
  }

  if (!effective) {
    return (
      <Alert variant="warning" className="mb-3">
        No effective PIM category payload was returned for this product.
      </Alert>
    )
  }

  return (
    <Card className="mb-3 border-secondary-subtle">
      <Card.Body>
        <Card.Title className="h6">PIM categories (product)</Card.Title>
        <p className="small text-muted mb-2">
          Inherited from product family <strong>{effective.productFamily?.trim() || '*'}</strong>, then adjusted here.
          Categories must exist in the master list (PIM categories page).
        </p>
        <div className="small mb-2">
          <span className="text-muted">Effective for this product:</span>{' '}
          {effective.effectiveCategories.map((c: PimMasterCategoryDto) => c.code).join(', ') || '—'}
        </div>
        {effective.familyCategories.length > 0 && (
          <div className="mb-2">
            <div className="small fw-semibold mb-1">Exclude from family set</div>
            {effective.familyCategories.map((c) => (
              <Form.Check
                key={c.id}
                type="checkbox"
                id={`pim-ex-${productId}-${c.id}`}
                label={c.displayName || c.code}
                checked={excludeIds.has(c.id)}
                onChange={(e) => toggle(c.id, 'exclude', e.target.checked)}
              />
            ))}
          </div>
        )}
        {extraCandidates.length > 0 && (
          <div className="mb-3">
            <div className="small fw-semibold mb-1">Also include (from master catalog)</div>
            {extraCandidates.map((c) => (
              <Form.Check
                key={c.id}
                type="checkbox"
                id={`pim-in-${productId}-${c.id}`}
                label={c.displayName || c.code}
                checked={includeIds.has(c.id)}
                onChange={(e) => toggle(c.id, 'include', e.target.checked)}
              />
            ))}
          </div>
        )}
        <Button size="sm" variant="primary" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
          {saveMutation.isPending ? 'Saving…' : 'Save product categories'}
        </Button>
      </Card.Body>
    </Card>
  )
}
